import React, { useState } from 'react';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';

const HistoryQuestionsList = ({ questions, onEdit, onDelete, onViewAnswers }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const totalPages = Math.ceil(questions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentQuestions = questions.slice(startIndex, startIndex + itemsPerPage);

  const getQuestionTypeColor = (type) => {
    const colors = {
      'number': 'bg-blue-100 text-blue-800',
      'text': 'bg-green-100 text-green-800',
      'single_select': 'bg-purple-100 text-purple-800',
      'multi_select': 'bg-orange-100 text-orange-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getRequiredBadge = (isRequired) => {
    return isRequired ? (
      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">
        Required
      </span>
    ) : (
      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
        Optional
      </span>
    );
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
        Active
      </span>
    ) : (
      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
        Inactive
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Position</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Question</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Type</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Required</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentQuestions.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="text-gray-400 mb-2">
                      <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 font-medium">No questions found</p>
                    <p className="text-gray-400 text-sm">Create your first question to get started</p>
                  </div>
                </td>
              </tr>
            ) : (
              currentQuestions.map((question, index) => (
                <tr 
                  key={question.id} 
                  className="border-b hover:bg-blue-50 transition-colors duration-200"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full text-sm font-semibold">
                        {question.position}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900 max-w-xs truncate">{question.question_text}</p>
                      {question.description && (
                        <p className="text-sm text-gray-500 mt-1 max-w-xs truncate">{question.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getQuestionTypeColor(question.question_type)}`}>
                      {question.question_type.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {getRequiredBadge(question.is_required)}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(question.is_active)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => onViewAnswers(question)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors duration-200 hover:shadow-md"
                        title="View Answers"
                      >
                        <FaEye size={16} />
                      </button>
                      <button
                        onClick={() => onEdit(question)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 hover:shadow-md"
                        title="Edit"
                      >
                        <FaEdit size={16} />
                      </button>
                      <button
                        onClick={() => onDelete(question.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 hover:shadow-md"
                        title="Delete"
                      >
                        <FaTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 py-4 border-t bg-gray-50">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-2 border rounded-lg transition-colors ${
                currentPage === page
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white border-orange-600'
                  : 'border-gray-300 hover:bg-gray-100'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default HistoryQuestionsList;
